import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  ArrowLeft, 
  Save, 
  AlertCircle, 
  Music as MusicIcon,
  Image as ImageIcon,
  FileAudio,
  Upload,
  X,
  CheckCircle,
  PlayCircle,
  Users,
  Tag,
  Calendar,
  DollarSign,
  Clock,
  AlertTriangle,
  Volume2
} from 'lucide-react';
import { musicService, digitalOceanService } from '../services/api';

const AdminEditMusic = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const audioRef = useRef(null);
  
  const { data: music, isLoading, error } = useQuery({
    queryKey: ['music', id],
    queryFn: () => musicService.getMusic(id),
  });
  
  const [genres, setGenres] = useState([]);
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const [uploadProgress, setUploadProgress] = useState({
    cover: { progress: 0, status: 'idle', fileName: '', error: null },
    previewAudio: { progress: 0, status: 'idle', fileName: '', error: null },
    fullAudio: { progress: 0, status: 'idle', fileName: '', error: null }
  });
  
  const [audioPreview, setAudioPreview] = useState({
    isPlaying: false,
    currentTime: 0,
    duration: 0
  });
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    artist_id: '',
    genre: '',
    featuring: [],
    price: '',
    age_rating: 'all',
    release_date: new Date().toISOString().split('T')[0],
    status: 'approved',
    is_coming_soon: false,
    artist_name: '',
    artist_email: ''
  });
  
  const [uploadedFiles, setUploadedFiles] = useState({
    coverUrl: '',
    coverPreview: '',
    previewAudioUrl: '',
    fullAudioUrl: '',
  });
  
  const [audioMetadata, setAudioMetadata] = useState({
    duration: 0,
    previewDuration: 90,
    bitrate: 320,
    fileSize: 0
  });
  
  // Fetch genres, artists, and music data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [genresResponse, artistsResponse] = await Promise.all([
          musicService.getGenres(),
          musicService.getArtists()
        ]);
        
        setGenres(genresResponse || []);
        setArtists(artistsResponse || []);
        
      } catch (error) {
        console.error('Error fetching data:', error);
        setGenres([
          { id: 1, name: 'Hip Hop' },
          { id: 2, name: 'Afrobeats' },
          { id: 3, name: 'Highlife' },
          { id: 4, name: 'Gospel' },
          { id: 5, name: 'R&B' },
          { id: 6, name: 'Pop' },
        ]);
        setArtists([]);
      }
    };

    fetchData();
  }, []);
  
  // Populate form when music data loads
  useEffect(() => {
    if (music) {
      console.log('Music data loaded:', music);
      setFormData({
        title: music.title || '',
        description: music.description || '',
        artist_id: music.artist?.id || '',
        artist_name: music.artist?.stage_name || '',
        artist_email: '',
        genre: music.genre?.id || music.genre || '',
        price: music.price || '',
        age_rating: music.age_rating || 'all',
        release_date: music.release_date?.split('T')[0] || new Date().toISOString().split('T')[0],
        status: music.status || 'approved',
        is_coming_soon: music.is_coming_soon || false,
        featuring: music.featuring_details?.map(a => a.id) || [],
      });
      
      setUploadedFiles({
        coverUrl: music.cover_image_url || '',
        coverPreview: music.cover_image_url || '',
        previewAudioUrl: music.preview_audio_url || '',
        fullAudioUrl: music.full_audio_url || '',
      });
      
      setAudioMetadata({
        duration: music.duration || 0,
        previewDuration: music.preview_duration || 90,
        bitrate: music.bitrate || 320,
        fileSize: music.file_size || 0
      });
    }
  }, [music]);
  
  const updateMutation = useMutation({
    mutationFn: (data) => musicService.adminUpdateMusic(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['music', id]);
      queryClient.invalidateQueries(['music']);
      alert('Music updated successfully!');
      navigate('/admin/music');
    },
    onError: (error) => {
      alert(`Update failed: ${error.message}`);
      setLoading(false);
    }
  });
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  const handleArtistChange = (e) => {
    const value = e.target.value;
    setFormData({
      ...formData,
      artist_id: value
    });
    
    if (value && value !== 'new') {
      setFormData(prev => ({
        ...prev,
        artist_name: '',
        artist_email: ''
      }));
    }
  };
  
  const handleFeaturingChange = (artistId) => {
    setFormData(prev => {
      const isSelected = prev.featuring.includes(artistId);
      if (isSelected) {
        return {
          ...prev,
          featuring: prev.featuring.filter(id => id !== artistId)
        };
      } else {
        return {
          ...prev,
          featuring: [...prev.featuring, artistId]
        };
      }
    });
  };
  
  const ageRatings = [
    { value: 'all', label: 'All Ages (0+)' },
    { value: 'teen', label: 'Teen (13-17)' },
    { value: 'adult', label: 'Adult (18+)' },
  ];
  
  // File upload handlers
  const handleCoverUpload = async (file) => {
    if (!file) return;

    try {
      setUploadProgress(prev => ({
        ...prev,
        cover: { progress: 0, status: 'uploading', fileName: file.name, error: null }
      }));

      const previewUrl = URL.createObjectURL(file);
      setUploadedFiles(prev => ({
        ...prev,
        coverPreview: previewUrl
      }));

      const uploadData = await digitalOceanService.generateMusicCoverUploadURL({
        filename: file.name
      });

      if (!uploadData.success) {
        throw new Error(uploadData.error || 'Failed to get upload URL');
      }

      await digitalOceanService.uploadToSpaces(
        uploadData.upload_url,
        uploadData.fields,
        file
      );

      setUploadProgress(prev => ({
        ...prev,
        cover: { ...prev.cover, status: 'success', progress: 100 }
      }));

      setUploadedFiles(prev => ({
        ...prev,
        coverUrl: uploadData.public_url
      }));

    } catch (error) {
      setUploadProgress(prev => ({
        ...prev,
        cover: { 
          ...prev.cover, 
          status: 'error', 
          error: error.message 
        }
      }));

      if (uploadedFiles.coverPreview) {
        URL.revokeObjectURL(uploadedFiles.coverPreview);
      }

      setUploadedFiles(prev => ({
        ...prev,
        coverPreview: ''
      }));
    }
  };

  const handleAudioUpload = async (file, type) => {
    if (!file) return;

    try {
      setUploadProgress(prev => ({
        ...prev,
        [type]: { progress: 0, status: 'uploading', fileName: file.name, error: null }
      }));

      const uploadData = await digitalOceanService.generateMusicAudioUploadURL({
        filename: file.name,
        audio_type: type === 'previewAudio' ? 'preview' : 'full'
      });

      if (!uploadData.success) {
        throw new Error(uploadData.error || 'Failed to get upload URL');
      }

      await digitalOceanService.uploadToSpaces(
        uploadData.upload_url,
        uploadData.fields,
        file
      );

      setUploadProgress(prev => ({
        ...prev,
        [type]: { ...prev[type], status: 'success', progress: 100 }
      }));

      setUploadedFiles(prev => ({
        ...prev,
        [`${type}Url`]: uploadData.public_url
      }));

      if (type === 'previewAudio') {
        const audio = new Audio();
        audio.onloadedmetadata = () => {
          setAudioMetadata(prev => ({
            ...prev,
            previewDuration: Math.min(Math.round(audio.duration), 90)
          }));
        };
        audio.src = uploadData.public_url;
      }

      if (type === 'fullAudio') {
        setAudioMetadata(prev => ({
          ...prev,
          fileSize: file.size
        }));
      }

    } catch (error) {
      setUploadProgress(prev => ({
        ...prev,
        [type]: { 
          ...prev[type], 
          status: 'error', 
          error: error.message 
        }
      }));
    }
  };
  
  const handleFileSelect = (type) => (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (type === 'cover') {
      handleCoverUpload(file);
    } else if (type === 'previewAudio' || type === 'fullAudio') {
      handleAudioUpload(file, type);
    }
  };
  
  const clearCoverImage = () => {
    if (uploadedFiles.coverPreview) {
      URL.revokeObjectURL(uploadedFiles.coverPreview);
    }
    
    setUploadedFiles(prev => ({
      ...prev,
      coverUrl: '',
      coverPreview: ''
    }));
    
    setUploadProgress(prev => ({
      ...prev,
      cover: { progress: 0, status: 'idle', fileName: '', error: null }
    }));
  };
  
  const clearAudioFile = (type) => {
    setUploadedFiles(prev => ({
      ...prev,
      [`${type}Url`]: ''
    }));
    
    setUploadProgress(prev => ({
      ...prev,
      [type]: { progress: 0, status: 'idle', fileName: '', error: null }
    }));
  };
  
  const handlePlayPreview = () => {
    if (!uploadedFiles.previewAudioUrl) return;
    
    if (audioRef.current) {
      if (audioPreview.isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setAudioPreview(prev => ({ ...prev, isPlaying: !prev.isPlaying }));
    }
  };
  
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setLoading(true);
    
    try {
      const updateData = {
        ...formData,
        cover_image_url: uploadedFiles.coverUrl,
        preview_audio_url: uploadedFiles.previewAudioUrl,
        full_audio_url: uploadedFiles.fullAudioUrl,
        duration: audioMetadata.duration,
        preview_duration: audioMetadata.previewDuration,
        bitrate: audioMetadata.bitrate,
        file_size: audioMetadata.fileSize,
      };
      
      updateMutation.mutate(updateData);
      
    } catch (error) {
      console.error('Error updating music:', error);
      alert(`Update failed: ${error.message}`);
      setLoading(false);
    }
  };
  
  const UploadProgressBar = ({ type, progress, fileName, status, error }) => {
    return (
      <div className="mt-3 bg-gray-50 rounded-lg p-3 border border-gray-200">
        <div className="flex items-center justify-between mb-1">
          <div className="text-sm font-medium text-gray-700 truncate">
            {fileName}
          </div>
          <div className="text-sm text-gray-500">
            {status === 'uploading' ? `${progress}%` : status}
          </div>
        </div>
        
        {status === 'uploading' && (
          <div className="w-full bg-gray-200 rounded-full h-1.5">
            <div 
              className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}
        
        {status === 'success' && (
          <div className="flex items-center text-green-600 text-sm">
            <CheckCircle className="h-4 w-4 mr-1" />
            Upload complete
          </div>
        )}
        
        {status === 'error' && (
          <div className="flex items-center text-red-600 text-sm">
            <AlertCircle className="h-4 w-4 mr-1" />
            {error}
          </div>
        )}
      </div>
    );
  };
  
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1d1d1d]"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-16 w-16 text-red-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Error loading music</h3>
        <p className="text-gray-600">{error.message}</p>
        <button
          onClick={() => navigate('/admin/music')}
          className="mt-4 bg-[#1d1d1d] text-white px-6 py-3 rounded-xl font-semibold"
        >
          Back to Music
        </button>
      </div>
    );
  }
  
  return (
    <div className="min-h-full bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin/music')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="h-5 w-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  Edit Music: {music?.title}
                </h1>
                <p className="text-gray-600 font-medium">
                  Update music details, files, and metadata
                </p>
              </div>
            </div>
            <div className="text-sm text-gray-500 bg-gray-100 px-4 py-2 rounded-xl font-semibold">
              SVP ID: {music?.svp_id || 'N/A'}
            </div>
          </div>
        </div>
        
        {/* Specifications Card */}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-300 rounded-xl p-4 mb-6">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-blue-800 font-bold mb-2">
                ⚠️ FILE UPLOAD REQUIREMENTS
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="flex items-center text-sm text-blue-700 font-medium">
                  <ImageIcon className="h-4 w-4 mr-2" />
                  <span><strong>Cover Image:</strong> Square (min 300×300px, max 5MB)</span>
                </div>
                <div className="flex items-center text-sm text-blue-700 font-medium">
                  <Volume2 className="h-4 w-4 mr-2" />
                  <span><strong>Audio Formats:</strong> MP3, WAV, OGG, M4A</span>
                </div>
                <div className="flex items-center text-sm text-blue-700">
                  <Clock className="h-4 w-4 mr-2" />
                  <span><strong>Preview Audio:</strong> Max 90 seconds</span>
                </div>
                <div className="flex items-center text-sm text-blue-700">
                  <FileAudio className="h-4 w-4 mr-2" />
                  <span><strong>Full Audio:</strong> Max 50MB</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Edit Form */}
        <div className="bg-white rounded-2xl shadow-xs border border-gray-200 overflow-hidden">
          <form onSubmit={handleSubmit} className="p-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left Column - Music Details */}
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <MusicIcon className="h-5 w-5 mr-2 text-[#1d1d1d]" />
                    Music Information
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Title *
                      </label>
                      <input
                        type="text"
                        name="title"
                        required
                        value={formData.title}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-colors"
                        placeholder="Enter music title"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description *
                      </label>
                      <textarea
                        name="description"
                        required
                        value={formData.description}
                        onChange={handleChange}
                        rows="3"
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-colors resize-none"
                        placeholder="Describe the music..."
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Genre
                        </label>
                        <select
                          name="genre"
                          value={formData.genre}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-colors"
                        >
                          <option value="">Select Genre</option>
                          {genres.map((genre) => (
                            <option key={genre.id} value={genre.id}>
                              {genre.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Age Rating *
                        </label>
                        <select
                          name="age_rating"
                          required
                          value={formData.age_rating}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-colors"
                        >
                          {ageRatings.map((rating) => (
                            <option key={rating.value} value={rating.value}>
                              {rating.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Price (GHS) *
                        </label>
                        <input
                          type="number"
                          name="price"
                          required
                          min="0"
                          step="0.01"
                          value={formData.price}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-colors"
                          placeholder="0.00"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Release Date
                        </label>
                        <input
                          type="date"
                          name="release_date"
                          value={formData.release_date}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-colors"
                        />
                      </div>
                    </div>

                    {/* Featuring Artists */}
                    {artists.length > 0 && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Featuring Artists (Optional)
                        </label>
                        <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto p-2">
                          {artists.map((artist) => (
                            <label
                              key={artist.id}
                              className={`flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${
                                formData.featuring.includes(artist.id)
                                  ? 'bg-blue-50 border-blue-300'
                                  : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={formData.featuring.includes(artist.id)}
                                onChange={() => handleFeaturingChange(artist.id)}
                                className="mr-3 h-4 w-4 text-blue-600"
                              />
                              <Users className="h-4 w-4 mr-2 text-gray-500" />
                              <span className="text-sm truncate">{artist.stage_name || artist.name}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Right Column - Artist & Files */}
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <Users className="h-5 w-5 mr-2 text-[#1d1d1d]" />
                    Artist Information
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select Artist *
                      </label>
                      <select
                        name="artist_id"
                        value={formData.artist_id}
                        onChange={handleArtistChange}
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-colors"
                      >
                        <option value="">Select Existing Artist</option>
                        {artists.map((artist) => (
                          <option key={artist.id} value={artist.id}>
                            {artist.stage_name || artist.name}
                          </option>
                        ))}
                        <option value="new">➕ Add New Artist</option>
                      </select>
                    </div>

                    {formData.artist_id === 'new' && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Artist Name *
                          </label>
                          <input
                            type="text"
                            name="artist_name"
                            value={formData.artist_name}
                            onChange={handleChange}
                            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-colors"
                            placeholder="Enter artist's name"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Artist Email *
                          </label>
                          <input
                            type="email"
                            name="artist_email"
                            value={formData.artist_email}
                            onChange={handleChange}
                            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-colors"
                            placeholder="Enter artist's email"
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <ImageIcon className="h-5 w-5 mr-2 text-[#1d1d1d]" />
                    Update Music Files
                  </h3>
                  
                  <div className="space-y-6">
                    {/* Cover Image Upload */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Cover Image * <span className="text-blue-600 text-xs">(Square, min 300×300px)</span>
                      </label>
                      
                      {/* Current Image Preview */}
                      {(uploadedFiles.coverUrl || uploadedFiles.coverPreview) && (
                        <div className="mb-4">
                          <div className="text-xs text-gray-500 mb-2">Current Image:</div>
                          <div className="relative w-32 h-32">
                            <img
                              src={uploadedFiles.coverPreview || uploadedFiles.coverUrl}
                              alt="Current cover"
                              className="w-full h-full object-cover rounded-lg shadow-md border border-gray-300"
                              style={{ aspectRatio: '1/1' }}
                            />
                            <button
                              type="button"
                              onClick={clearCoverImage}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        </div>
                      )}
                      
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-gray-400 transition-colors">
                        <input
                          type="file"
                          id="cover-upload"
                          accept="image/*"
                          onChange={handleFileSelect('cover')}
                          className="hidden"
                        />
                        <label
                          htmlFor="cover-upload"
                          className="cursor-pointer block"
                        >
                          <div className="space-y-2">
                            <ImageIcon className="h-8 w-8 text-gray-400 mx-auto" />
                            <p className="text-sm text-gray-600">
                              {uploadedFiles.coverUrl ? 'Replace cover image' : 'Upload new cover image'}
                            </p>
                            <p className="text-xs text-gray-500">
                              Click to upload
                            </p>
                          </div>
                        </label>
                      </div>
                      
                      {uploadProgress.cover.status !== 'idle' && (
                        <UploadProgressBar
                          type="cover"
                          progress={uploadProgress.cover.progress}
                          fileName={uploadProgress.cover.fileName}
                          status={uploadProgress.cover.status}
                          error={uploadProgress.cover.error}
                        />
                      )}
                    </div>

                    {/* Full Audio Upload */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Full Audio File *
                      </label>
                      
                      {uploadedFiles.fullAudioUrl && (
                        <div className="mb-3 bg-gray-50 rounded-lg p-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <FileAudio className="h-5 w-5 text-green-500" />
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  Current audio file
                                </p>
                                <p className="text-xs text-gray-500">
                                  Click "Upload new" to replace
                                </p>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => clearAudioFile('fullAudio')}
                              className="text-xs text-red-600 hover:text-red-700 font-medium"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      )}
                      
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-gray-400 transition-colors">
                        <input
                          type="file"
                          id="full-audio-upload"
                          accept=".mp3,.wav,.ogg,.m4a"
                          onChange={handleFileSelect('fullAudio')}
                          className="hidden"
                        />
                        <label
                          htmlFor="full-audio-upload"
                          className="cursor-pointer block"
                        >
                          <div className="space-y-2">
                            <FileAudio className="h-8 w-8 text-gray-400 mx-auto" />
                            <p className="text-sm text-gray-600">
                              {uploadedFiles.fullAudioUrl ? 'Upload new full audio' : 'Upload full audio'}
                            </p>
                            <p className="text-xs text-gray-500">
                              MP3, WAV, OGG, M4A (max 50MB)
                            </p>
                          </div>
                        </label>
                      </div>
                      
                      {uploadProgress.fullAudio.status !== 'idle' && (
                        <UploadProgressBar
                          type="fullAudio"
                          progress={uploadProgress.fullAudio.progress}
                          fileName={uploadProgress.fullAudio.fileName}
                          status={uploadProgress.fullAudio.status}
                          error={uploadProgress.fullAudio.error}
                        />
                      )}
                    </div>

                    {/* Preview Audio Upload */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Preview Audio * <span className="text-blue-600 text-xs">(Max 90 seconds)</span>
                      </label>
                      
                      {/* Preview Player */}
                      {uploadedFiles.previewAudioUrl && (
                        <div className="mb-4 bg-gray-50 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center space-x-3">
                              <button
                                onClick={handlePlayPreview}
                                className="w-10 h-10 rounded-full bg-[#1d1d1d] text-white flex items-center justify-center hover:bg-gray-800 transition-colors"
                              >
                                {audioPreview.isPlaying ? (
                                  <div className="h-4 w-4 bg-white"></div>
                                ) : (
                                  <PlayCircle className="h-5 w-5" />
                                )}
                              </button>
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  Current preview audio
                                </p>
                                <p className="text-xs text-gray-500">
                                  Click to play preview
                                </p>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => clearAudioFile('previewAudio')}
                              className="text-xs text-red-600 hover:text-red-700 font-medium"
                            >
                              Remove
                            </button>
                          </div>
                          <audio
                            ref={audioRef}
                            src={uploadedFiles.previewAudioUrl}
                            onTimeUpdate={(e) => setAudioPreview(prev => ({ 
                              ...prev, 
                              currentTime: e.target.currentTime,
                              duration: e.target.duration 
                            }))}
                            onEnded={() => setAudioPreview(prev => ({ ...prev, isPlaying: false }))}
                            preload="metadata"
                          />
                        </div>
                      )}
                      
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-gray-400 transition-colors">
                        <input
                          type="file"
                          id="preview-audio-upload"
                          accept=".mp3,.wav,.ogg,.m4a"
                          onChange={handleFileSelect('previewAudio')}
                          className="hidden"
                        />
                        <label
                          htmlFor="preview-audio-upload"
                          className="cursor-pointer block"
                        >
                          <div className="space-y-2">
                            <FileAudio className="h-8 w-8 text-gray-400 mx-auto" />
                            <p className="text-sm text-gray-600">
                              {uploadedFiles.previewAudioUrl ? 'Upload new preview audio' : 'Upload preview audio'}
                            </p>
                            <p className="text-xs text-gray-500">
                              Max 90 seconds, 10MB
                            </p>
                          </div>
                        </label>
                      </div>
                      
                      {uploadProgress.previewAudio.status !== 'idle' && (
                        <UploadProgressBar
                          type="previewAudio"
                          progress={uploadProgress.previewAudio.progress}
                          fileName={uploadProgress.previewAudio.fileName}
                          status={uploadProgress.previewAudio.status}
                          error={uploadProgress.previewAudio.error}
                        />
                      )}
                    </div>
                  </div>
                </div>

                {/* Status Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-colors"
                  >
                    <option value="approved">Approved (Published)</option>
                    <option value="pending">Pending Review</option>
                    <option value="draft">Draft</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>

                {/* Coming Soon Toggle */}
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="is_coming_soon"
                    name="is_coming_soon"
                    checked={formData.is_coming_soon}
                    onChange={handleChange}
                    className="h-4 w-4 text-[#1d1d1d]"
                  />
                  <label htmlFor="is_coming_soon" className="text-sm font-medium text-gray-700">
                    Mark as Coming Soon
                  </label>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading || updateMutation.isLoading}
                  className="w-full bg-gradient-to-r from-green-600 to-green-700 text-white py-4 rounded-lg font-semibold hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {loading || updateMutation.isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Updating...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Update Music
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminEditMusic;